import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { isSubscriptionsEnabled } from '@/utils/featureFlags'
import { ROUTES } from '../routes'

interface ConditionalRouteProps {
  children: React.ReactNode
  requiresSubscriptions?: boolean
  fallbackPath?: string
}

/**
 * ConditionalRoute Guard
 *
 * Handles feature flag-based route rendering.
 * Hides subscription-related routes when the subscriptions feature is disabled.
 *
 * Example usage:
 * <ConditionalRoute>
 *   <PricingPage />
 * </ConditionalRoute>
 */
export const ConditionalRoute: React.FC<ConditionalRouteProps> = ({
  children,
  requiresSubscriptions = true,
  fallbackPath = ROUTES.HOME,
}) => {
  const navigate = useNavigate()

  useEffect(() => {
    // If route requires subscriptions but feature is disabled, redirect
    if (requiresSubscriptions && !isSubscriptionsEnabled()) {
      navigate(fallbackPath, { replace: true })
    }
  }, [requiresSubscriptions, fallbackPath, navigate])

  // If feature is disabled and route requires it, don't render
  if (requiresSubscriptions && !isSubscriptionsEnabled()) {
    return null
  }

  // Feature is enabled or not required, render the route
  return <>{children}</>
}
